import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

function compareValues(a, b) {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';

  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const aNum = Number(a);
  const bNum = Number(b);
  const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum) && String(a).trim() !== '' && String(b).trim() !== '';

  if (bothNumeric) {
    if (aNum < bNum) return -1;
    if (aNum > bNum) return 1;
    return 0;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function ICSPage() {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [mainRegister, setMainRegister] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState('desc');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  const fetchWithBackendFallback = async (path, options) => {
    const primary = await fetch(path, options);
    if (primary.status !== 404) {
      return primary;
    }

    // Fallback for cases where frontend proxy is not active.
    return fetch(`http://localhost:3001${path}`, options);
  };

  const loadRegister = useCallback(async () => {
    setLoading(true);
    setStatus('Loading ICS transactions...');

    try {
      const response = await fetchWithBackendFallback('/api/ics');
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      setMainRegister(data || []);
      setStatus(`Loaded ${Array.isArray(data) ? data.length : 0} ICS transactions.`);
    } catch (error) {
      console.error('Error loading ICS register:', error);
      setMainRegister([]);
      setStatus('Failed to load ICS transactions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load ICS accounts on mount
    const loadAccounts = async () => {
      try {
        const res = await fetch('/api/accounts');
        const data = await res.json();
        const icsAccounts = data.filter((a) => a.bank_id === 'ICS');
        setAccounts(icsAccounts);
        if (icsAccounts.length > 0) {
          setSelectedAccount(String(icsAccounts[0].id));
        }
      } catch (err) {
        console.error('Failed to load accounts:', err);
      }
    };
    loadAccounts();
    loadRegister();
  }, [loadRegister]);

  // Build dynamic title with account details
  const accountTypeLabels = { own: 'Own', joint: 'Joint' };
  const selectedAcc = accounts.find((a) => String(a.id) === selectedAccount);
  const displayTitle = selectedAcc 
    ? `ICS Import - ${selectedAcc.currency} - ${accountTypeLabels[selectedAcc.account_type] || selectedAcc.account_type}`
    : 'ICS Import';

  const handleImport = async () => {
    if (!pasteText.trim()) {
      setStatus('Paste ICS text before importing.');
      return;
    }

    setLoading(true);
    setStatus('Parsing and importing ICS text...');

    try {
      const response = await fetchWithBackendFallback('/api/ics/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pasteText })
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('ICS route not found on backend. Restart backend server so /api/ics is loaded.');
        }
        const errorBody = await response.json().catch(() => null);
        const message = errorBody?.error || `Server responded with ${response.status}`;
        throw new Error(message);
      }

      const result = await response.json();
      setMainRegister(result.mainRegister || []);
      setStatus(
        `Parsed ${result.parsedCount} rows, imported ${result.addedCount}, skipped ${result.duplicateCount} duplicates.`
      );
    } catch (error) {
      console.error('Error importing ICS text:', error);
      setStatus(`Error during import: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortOrder('asc');
    setPage(0);
  };

  const sortedRows = useMemo(() => {
    const rows = [...mainRegister];

    rows.sort((rowA, rowB) => {
      const result = compareValues(rowA?.[sortBy], rowB?.[sortBy]);
      return sortOrder === 'asc' ? result : -result;
    });

    return rows;
  }, [mainRegister, sortBy, sortOrder]);

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
    >
      <ASTITitle text={displayTitle} />
      <ASTIText text="Paste raw ICS website text and import transactions directly into the transactions table." />

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mt={2} alignItems="center">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Account</InputLabel>
          <Select
            value={selectedAccount}
            label="Account"
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={String(account.id)}>
                {account.currency} - {accountTypeLabels[account.account_type] || account.account_type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleImport} disabled={loading}>
          Import Pasted Text
        </Button>
        <Button variant="outlined" onClick={loadRegister} disabled={loading}>
          {loading ? 'Loading...' : 'Reload ICS Register'}
        </Button>
      </Box>
      <Box sx={{ width: '95%', maxWidth: 1200, mt: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={14}
          label="Paste ICS text here"
          value={pasteText}
          onChange={(event) => setPasteText(event.target.value)}
          disabled={loading}
          sx={{
            '& .MuiInputLabel-root': {
              color: '#b0b0b0'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#90caf9'
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              color: '#111111',
              '& fieldset': {
                borderColor: '#666666'
              },
              '&:hover fieldset': {
                borderColor: '#90caf9'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#90caf9'
              }
            },
            '& .MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline': {
              color: '#111111'
            }
          }}
        />
      </Box>


      {status && <ASTIText text={status} />}

      {mainRegister.length > 0 ? (
        <Box sx={{ width: '95%', marginTop: '20px', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
          <Table sx={{ backgroundColor: '#fff', minWidth: '100%' }} size="small">
            <TableHead>
              <TableRow>
                {Object.keys(mainRegister[0]).map((key) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontWeight: 'bold',
                      color: '#000',
                      backgroundColor: '#f5f5f5',
                      padding: '8px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={key}
                  >
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortOrder : 'asc'}
                      onClick={() => handleSort(key)}
                    >
                      {key}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  {Object.values(row).map((value, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        color: '#000',
                        padding: '8px',
                        fontSize: '12px',
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={String(value)}
                    >
                      {String(value)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ backgroundColor: '#fafafa' }}
          />
        </Box>
      ) : (
        !loading && <ASTIText text="No ICS records found." />
      )}
    </Box>
  );
}
